/**
 * NBA Team Name to 3-Letter Code Mapping
 * Maps full team names to their standard 3-letter abbreviations
 */
export const NBA_TEAM_CODES: Record<string, string> = {
  'Atlanta Hawks': 'ATL',
  'Boston Celtics': 'BOS',
  'Brooklyn Nets': 'BKN',
  'Charlotte Hornets': 'CHA',
  'Chicago Bulls': 'CHI',
  'Cleveland Cavaliers': 'CLE',
  'Dallas Mavericks': 'DAL',
  'Denver Nuggets': 'DEN',
  'Detroit Pistons': 'DET',
  'Golden State Warriors': 'GSW',
  'Houston Rockets': 'HOU',
  'Indiana Pacers': 'IND',
  'Los Angeles Clippers': 'LAC',
  'Los Angeles Lakers': 'LAL',
  'Memphis Grizzlies': 'MEM',
  'Miami Heat': 'MIA',
  'Milwaukee Bucks': 'MIL',
  'Minnesota Timberwolves': 'MIN',
  'New Orleans Pelicans': 'NOP',
  'New York Knicks': 'NYK',
  'Oklahoma City Thunder': 'OKC',
  'Orlando Magic': 'ORL',
  'Philadelphia 76ers': 'PHI',
  'Phoenix Suns': 'PHX',
  'Portland Trail Blazers': 'POR',
  'Sacramento Kings': 'SAC',
  'San Antonio Spurs': 'SAS',
  'Toronto Raptors': 'TOR',
  'Utah Jazz': 'UTA',
  'Washington Wizards': 'WSH',
};

/** NCAA Men's Basketball: full name/nickname -> 3-letter school code */
const NCAA_MEN_BASKETBALL_CODES: Record<string, string> = {
  seminoles: 'FSU',
  'florida state': 'FSU',
  'florida state seminoles': 'FSU',
  'blue devils': 'DUK',
  duke: 'DUK',
  'kentucky wildcats': 'UK',
  kentucky: 'UK',
  'tar heels': 'UNC',
  'north carolina': 'UNC',
  bruins: 'UCLA',
  ucla: 'UCLA',
  razorbacks: 'ARK',
  arkansas: 'ARK',
  jayhawks: 'KU',
  kansas: 'KU',
  buckeyes: 'OSU',
  'ohio state': 'OSU',
  'miami hurricanes': 'MIA',
  cyclones: 'ISU',
  'iowa state': 'ISU',
  zags: 'GON',
  'gonzaga bulldogs': 'GON',
  'michigan state': 'MSU',
  'michigan state bulldogs': 'MSU',
  hoosiers: 'IND',
  indiana: 'IND',
  byu: 'BYU',
  'byu cougars': 'BYU',
  boilermakers: 'PUR',
  purdue: 'PUR',
  'fighting irish': 'ND',
  'notre dame': 'ND',
  volunteers: 'TEN',
  tennessee: 'TEN',
  terrapins: 'MD',
  maryland: 'MD',
  cavaliers: 'UVA',
  'virginia cavaliers': 'UVA',
  'sun devils': 'ASU',
  'arizona state': 'ASU',
  'arizona wildcats': 'ARIZ',
  arizona: 'ARIZ',
  buffaloes: 'COL',
  colorado: 'COL',
  gators: 'UF',
  'florida gators': 'UF',
  gamecocks: 'SCAR',
  'south carolina gamecocks': 'SCAR',
  'south carolina': 'SCAR',
};

const normalize = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();

// Build a nickname -> code lookup so inputs like "Jazz" / "Clippers"
// map to the expected 3-letter codes (UTA / LAC), matching company standards.
const NBA_TEAM_NICKNAME_CODES: Record<string, string> = Object.fromEntries(
  Object.entries(NBA_TEAM_CODES).flatMap(([fullName, code]) => {
    const n = normalize(fullName);

    // Most teams can be matched by last word (e.g., "Jazz", "Clippers")
    const lastWord = n.split(' ').slice(-1)[0];

    // Handle known multi-word nicknames
    // - "Trail Blazers"
    // - "76ers"
    const multiWordNicknames: string[] = [];
    if (n.endsWith('trail blazers')) multiWordNicknames.push('trail blazers');
    if (n.endsWith('76ers')) multiWordNicknames.push('76ers');

    const keys = new Set<string>([n, lastWord, ...multiWordNicknames]);
    return Array.from(keys).map(k => [k, code] as const);
  })
);

/**
 * Get the 3-letter code for a team name
 * Falls back to generating abbreviation from name if not found in mapping
 * @param teamName - Full team name (e.g., "Utah Jazz")
 * @returns 3-letter code (e.g., "UTA") or generated abbreviation
 */
export function getTeamCode(teamName: string): string {
  if (!teamName) {
    return 'TBD';
  }

  const raw = teamName.trim();

  // If already a 3-letter code, keep it.
  if (/^[A-Z]{3}$/.test(raw)) return raw;

  // Try exact match first (as-provided key)
  if (NBA_TEAM_CODES[raw]) return NBA_TEAM_CODES[raw];

  const n = normalize(raw);

  // NCAA Men's Basketball: school name/nickname -> 3-letter code
  if (NCAA_MEN_BASKETBALL_CODES[n]) return NCAA_MEN_BASKETBALL_CODES[n];
  const ncaaNickname = n.split(' ').slice(-1)[0];
  if (NCAA_MEN_BASKETBALL_CODES[ncaaNickname]) return NCAA_MEN_BASKETBALL_CODES[ncaaNickname];

  // NBA: full name / nickname matches
  const nicknameCode = NBA_TEAM_NICKNAME_CODES[n];
  if (nicknameCode) return nicknameCode;

  // Fallback: generate abbreviation from name (first 4 chars or full name if shorter)
  return raw.length > 4 ? raw.substring(0, 4).toUpperCase() : raw.toUpperCase();
}
